/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('cart-line-item-status');
      this.currentTotalItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0
      );

      this.currentQuantities = [];
      this.querySelectorAll('.cart-item').forEach((item) => {
        this.currentQuantities[item.dataset.variantId] = Number(item.querySelector('.qty-input__input').value);
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-remove-item')) return;
      evt.preventDefault();
      this.updateQuantity(evt.target.dataset.index, 0);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.dataset.index, evt.target.value, document.activeElement.name);
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    async updateQuantity(line, quantity, name) {
      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);
      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      const sections = this.getSectionsToRender().map((section) => section.section);
      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      });

      try {
        const lineItem = document.getElementById(`cart-item-${line}`);
        const variantId = Number(lineItem.dataset.variantId);
        const oldTotalQuantity = this.currentTotalItemCount;
        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        const newTotalQuantity = data.item_count;

        if (this.cartDrawer) {
          cartDrawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);

          if (newTotalQuantity === 0) {
            const recommendations = this.cartDrawer.querySelector('product-recommendations');
            if (recommendations) recommendations.remove();
          }
        } else if (newTotalQuantity === 0) {
          // We're on the Cart page
          const cartTitle = this.closest('.cc-main-cart').querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartContent = document.getElementById('cart-summary-and-blocks');
          if (cartContent) cartContent.hidden = true;
        }

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const { selector } = section;
          const el = sectionEl.querySelector(selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data.sections[section.section], selector);
        });

        if (this.cartDrawer && newTotalQuantity === 0) {
          cartDrawerContent.classList.add('grow', 'flex', 'items-center');

          if (this.cartDrawer.querySelector('promoted-products')) {
            this.cartDrawer
              .querySelector('.drawer__content')
              .classList.toggle('drawer__empty-with-promotions', newTotalQuantity === 0);
          }
        }

        this.updateRecommendations(data.item_count > 0 ? data.items[0].product_id : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId,
              oldQuantity: this.currentQuantities[variantId],
              newQuantity: Number(quantity)
            }
          }));
        }

        this.currentQuantities[variantId] = Number(quantity);

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        this.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

        const input = document.getElementById(`quantity-${line}`);
        input.value = input.dataset.initialValue;
        input.closest('quantity-input').currentQty = input.dataset.initialValue;
      } finally {
        this.classList.remove('pointer-events-none');

        // Attempt to maintain the same scroll position in the cart drawer
        if (cartDrawerContent) {
          requestAnimationFrame(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; });
          setTimeout(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; }, 0);
          requestAnimationFrame(() => { this.cartDrawer.scrollTop = cartDrawerScroll; });
          setTimeout(() => { this.cartDrawer.scrollTop = cartDrawerScroll; }, 0);
        }
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    async refresh() {
      const errors = document.getElementById('cart-errors');
      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${[...new Set(sections)]}`);

        // The status is 400, refresh the whole cart and stop
        if (response.status === 400 && this.cartDrawer) {
          this.cartDrawer.refresh(true);
          return;
        }

        const data = await response.json();

        if (!response.ok) throw new Error(response.status);

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data[section.section], section.selector);
        });

        const firstCartItem = this.querySelector('.cart-item:first-child');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        errors.innerHTML = '';
        errors.hidden = true;
      } catch (error) {
        errors.textContent = theme.strings.cartError;
        errors.hidden = false;
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          }
        ];
      } else {
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          },
          {
            id: 'cart-summary',
            section: document.getElementById('cart-summary').dataset.section,
            selector: '.cart__summary'
          }
        ];
      }

      return sections;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.classList.add('pointer-events-none');

      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader`);
      if (loader) loader.hidden = false;

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          trapFocus(
            this.cartDrawer.querySelector('.js-cart-empty'),
            this.cartDrawer.querySelector('a')
          );
        } else if (this.cartDrawer.querySelector('.cart-item')) {
          trapFocus(this.cartDrawer, document.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}
class DraftOrderButton extends HTMLElement {
  constructor() {
    super();
    this.configs = this.getStoreConfigs();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  getStoreConfigs() {
    const SHOP_HOSTNAME_TPO = window.location.hostname;
    const storeDataProductOption = JSON.parse(document.getElementById('tpo-store-data')?.textContent || '{}');
    const storeLocale = window.Shopify?.country || 'en-US';
    const storeCurrency = window.Shopify?.currency?.active || 'USD';
    const customerId = storeDataProductOption?.customer?.id;
    const settingsTPO = window.TPOConfigs.settings || {};
    const showDiscountBoxOnCheckout = Boolean(Number(settingsTPO.showDiscountBoxOnCheckout || '0'));

    const configsSelectorTPO = settingsTPO?.selectorSettings
    ? JSON.parse(settingsTPO.selectorSettings)
    : {
          advanceSelectors: {}
      };

    const shouldUseMultiCurrency = !!configsSelectorTPO.advanceSelectors?.useMultiCurrency;
    const shouldUseMoneyCurrencyFormat = typeof storeUsingMoneyCurrencyFormat !== 'undefined' 
      && Array.isArray(storeUsingMoneyCurrencyFormat)
      && storeUsingMoneyCurrencyFormat.includes(SHOP_HOSTNAME_TPO);
    
    const MONEY_FORMAT_WITH_CURRENCY_TPO = storeDataProductOption?.shop?.money_with_currency_format || '${{amount}}';
    const MONEY_FORMAT_TPO = storeDataProductOption?.shop?.money_format || '${{amount}}';

    const discountInput = document.querySelector('.tpo-discount-code-block input.tpo-discount-code-input--input');
    const discountCode = discountInput?.value?.trim() || '';

    const {
        wholeSaleDiscountLabel,
        totalPriceMode,
        newAddOnPrice = 'no',
        newAddOnProduct = 'no',
        customOptionFor,
        changeProductPrice,
        taxRateField: taxRate,
        originalPriceLabelField: originalPriceLabel = 'Our price just',
        customOptionsDisplay = 'split',
        addOnProductsDisplay = 'hide',
    } = settingsTPO;

    return {
      SHOP_HOSTNAME_TPO,
      storeLocale,
      storeCurrency,
      customerId,
      showDiscountBoxOnCheckout,
      shouldUseMultiCurrency,
      discountCode,
      customOptionsDisplay,
      shouldUseMoneyCurrencyFormat,
      MONEY_FORMAT_WITH_CURRENCY_TPO,
      MONEY_FORMAT_TPO
    };
  }

  convertSettingMessage(message, replacedValue) {
    if (!message) return '';
    
    let regex = new RegExp(/{{(.*?)}}/gim);
    if (typeof replacedValue === 'object') {
      if (replacedValue.length > 1) {
        const newMessage = message.replace('{{minValue}}', replacedValue[0]);
        return newMessage.replace('{{maxValue}}', replacedValue[1]);
      }
      return message.replace(regex, function () {
        return replacedValue;
      });
    } else {
      return message.replace(regex, function () {
        return replacedValue;
      });
    }
  }

  isNumeric(str) {
      return !isNaN(str) && !isNaN(parseFloat(str));
  }

  getSeparator(locale = 'en-US') {
    const { shouldUseMultiCurrency } = this.configs;
        if (!shouldUseMultiCurrency)
            return {
                decimal: '.',
                group: ','
            };
        if (window.TPOGlobal && window.TPOGlobal.currencySeparator && window.TPOGlobal.currencySeparator[locale]) {
            return window.TPOGlobal.currencySeparator[locale];
        }
        const numberWithGroupAndDecimalSeparator = 10000.1;
        const formatParts = Intl.NumberFormat(locale).formatToParts(numberWithGroupAndDecimalSeparator);
        return {
            decimal: formatParts.find((part) => part.type === 'decimal')?.value || '.',
            group: formatParts.find((part) => part.type === 'group')?.value || ','
        };
    }

  getMoneyFormat() {
        const { shouldUseMoneyCurrencyFormat, MONEY_FORMAT_TPO, MONEY_FORMAT_WITH_CURRENCY_TPO } = this.getStoreConfigs();
        const rs = shouldUseMoneyCurrencyFormat ? MONEY_FORMAT_WITH_CURRENCY_TPO : MONEY_FORMAT_TPO;
        return rs.trim();
  }
  // ⭐ Thêm hàm convertMoneyToNumber
  convertMoneyToNumber(moneyStr, locale) {
    const { storeLocale } = this.configs;
    locale = locale || storeLocale;
        let formatString = this.getMoneyFormat();
        let placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const separator = this.getSeparator(locale);
        let thousands = separator.group;
        let decimal = separator.decimal;
        if (locale === storeLocale) {
            switch (formatString.match(placeholderRegex)[1]) {
                case 'amount_with_comma_separator':
                case 'amount_no_decimals_with_comma_separator':
                    thousands = '.';
                    decimal = ',';
                    break;
                case 'amount_no_decimals_with_space_separator':
                    thousands = ' ';
                    break;
                case 'amount_with_apostrophe_separator':
                    thousands = '\'';
                    break;
            }
        }
        let numberRegex = /\d+((\.|\'|\,| )\d+)*/g;
        let regexMatch = moneyStr.trim().match(numberRegex);
        if (regexMatch) {
            let tempPrice = regexMatch[0];
            tempPrice = tempPrice.replaceAll(thousands, '');
            tempPrice = tempPrice.replace(decimal, '.');
            tempPrice = tempPrice.replaceAll(' ', '');
            if (this.isNumeric(tempPrice)) {
                return parseFloat(tempPrice);
            }
        }

        return 0;
    }

  // ⭐ Thêm hàm convertMoneyToNumberV2
  convertMoneyToNumberV2(moneyStr, cartItem, configs) {
    const oldResult = this.convertMoneyToNumber(moneyStr, configs.storeLocale);
    if (!configs.shouldUseMultiCurrency) return oldResult;
    
    const property = cartItem.properties || {};
    const activeCurrency = property._tpo_active_currency;
    if (!activeCurrency) return oldResult;
    
    return this.convertMoneyToNumber(moneyStr, activeCurrency);
  }

  buildCartInput(cartJson, configs) {
    const {
      SHOP_HOSTNAME_TPO,
      storeLocale,
      storeCurrency,
      customerId,
      showDiscountBoxOnCheckout,
      shouldUseMultiCurrency,
      discountCode,
      customOptionsDisplay,
      shouldUseMoneyCurrencyFormat
    } = configs;

    const settingsTPO = window.TPOConfigs.settings || {};
    const {
      customOptionFor,
      wholeSaleDiscountLabel,
    } = settingsTPO;

    const priceModCart = {
      easifyUrl: `https://${SHOP_HOSTNAME_TPO}/apps/easify-po/`,
      tokenCart: '',
      cartJson: { tpo_add_price: 0, items: [] },
      tpoCartJson: cartJson,
      isCartEmpty: cartJson.items.length === 0,
    };

    // ⭐ Thêm _tpo_price và _tpo_custom_option vào hidden properties
    const hiddenProperties = [
      '_tpo_main_product_id',
      '_tpo_is_main_product',
      '_tpo_add_on_keys',
      '_tpo_add_on_key',
      '_tpo_add_on_variant_ids',
      '_tpo_wholesale',
      '_tpo_add_by'
    ];

    const isSplittingOptions = customOptionsDisplay === 'split';
    const shouldRequireShipping = false;
    const shouldApplyTax = true;

    let indexItem = 0;
    const lineItemsCustom = [];

    // ⭐ Fix helper function - cần return CENTS (không phải dollars)
    const parsePriceFromString = (str, cartItem) => {
      const match = str.match(/\[ \+([^\]]+)\]/);
      if (!match) return 0;
      
      let textMoney = match[1];
      let priceValue = 0;
      
      // ⭐ Dùng method của class
      priceValue = this.convertMoneyToNumber(textMoney, storeLocale);
      
      // ⭐ Nếu có multi-currency
      if (shouldUseMultiCurrency) {
        priceValue = this.convertMoneyToNumberV2(textMoney, cartItem, configs);
      }
      
      // ⭐ Convert to cents
      return parseFloat(priceValue * 100);
    };


    // Loop qua từng cart item
    cartJson.items.forEach((e) => {
      let itemProperties = e.properties || {};
      const propertiesEntries = Object.entries(itemProperties).length
        ? Object.entries(itemProperties).filter(([key, value]) => value !== '')
        : [];
      
      let propertiesOfLineItem = propertiesEntries?.map(([key, value]) => ({ key, value }));
      
      // ⭐ QUAN TRỌNG: Lọc bỏ ALL hidden properties (bao gồm _tpo_price và _tpo_custom_option)
      let propertiesOfCustomOptions = [...propertiesOfLineItem].filter(
        ({ key, value }) => !hiddenProperties.includes(key)
      );

      // Nếu không split options, filter ra các properties có giá
      if (!isSplittingOptions) {
        const hasPriceProperty = propertiesOfLineItem.findIndex((property) => {
          return /\[ .+ \]/g.test(property.value);
        }) > -1;
        
        if (hasPriceProperty) {
          // ⭐ Chỉ giữ lại hidden properties (để track nhưng không hiển thị)
          propertiesOfLineItem = propertiesOfLineItem.filter(({ key, value }) =>
            hiddenProperties.includes(key)
          );
        }
      }

      // 1. Thêm main product (với properties đã lọc bỏ hidden keys)
      lineItemsCustom.push({
        variantId: `gid://shopify/ProductVariant/${e.variant_id}`,
        productId: e.product_id,
        quantity: e.quantity,
        sku: e.sku,
        customAttributes: propertiesOfLineItem, // ⭐ Đã lọc bỏ hidden properties
        appliedDiscount: {
          title: wholeSaleDiscountLabel || 'Wholesale discount',
          valueType: 'FIXED_AMOUNT',
          value: 0,
          description: '',
        },
        originalUnitPrice: e.final_line_price / e.quantity,
        requiresShipping: shouldRequireShipping,
        taxable: shouldApplyTax,
        index: indexItem++,
        isFromOptionSet: false,
        key: e.key
      });

      // 2. Xử lý Custom Options (nếu có properties với giá)
      if (e.properties && Object.keys(e.properties).length > 0) {
        let totalCustomOptionPrice = 0;
        let customOptionAttributes = [];

        // ⭐ Chỉ process các properties KHÔNG phải hidden
        for (const property in e.properties) {
          // Skip hidden properties
          if (hiddenProperties.includes(property)) {
            continue;
          }

          let value = e.properties[property];
          
          if (typeof value === 'string' && /\[ \+.+\]/g.test(value)) {
            // Property có giá bổ sung
            const optionPrice = parsePriceFromString(value);
            totalCustomOptionPrice += optionPrice;
            
            customOptionAttributes.push({
              key: property,
              value: value
            });
          }
        }

        // 3. Tạo line item cho Custom Options nếu có giá
        if (totalCustomOptionPrice > 0) {
          // if (isSplittingOptions) {
          //   // Split mode: Tạo line item riêng cho từng option có giá
          //   customOptionAttributes.forEach(attr => {
          //     const optionPrice = parsePriceFromString(attr.value);
              
          //     lineItemsCustom.push({
          //       title: customOptionFor 
          //         ? this.convertSettingMessage(customOptionFor, e.title)
          //         : `Custom Option for ${e.title}`,
          //       originalUnitPrice: parseFloat(optionPrice / 100),
          //       quantity: e.quantity,
          //       requiresShipping: shouldRequireShipping,
          //       customAttributes: [attr], // ⭐ Không có hidden properties
          //       appliedDiscount: {
          //         title: wholeSaleDiscountLabel || 'Wholesale discount',
          //         valueType: 'FIXED_AMOUNT',
          //         value: 0,
          //         description: ''
          //       },
          //       taxable: shouldApplyTax,
          //       index: indexItem++,
          //       isFromOptionSet: true
          //     });
          //   });
          // } else {
          //   // Grouped mode: Gom tất cả options thành 1 line item
          //   lineItemsCustom.push({
          //     title: customOptionFor 
          //       ? this.convertSettingMessage(customOptionFor, e.title)
          //       : `Custom Option for ${e.title}`,
          //     originalUnitPrice: totalCustomOptionPrice,
          //     quantity: e.quantity,
          //     requiresShipping: shouldRequireShipping,
          //     customAttributes: propertiesOfCustomOptions, // ⭐ Đã lọc bỏ hidden properties
          //     appliedDiscount: {
          //       title: wholeSaleDiscountLabel || 'Wholesale discount',
          //       valueType: 'FIXED_AMOUNT',
          //       value: 0,
          //       description: ''
          //     },
          //     taxable: shouldApplyTax,
          //     index: indexItem++,
          //     isFromOptionSet: true
          //   });
          // }

          // Cập nhật priceModCart
          priceModCart.cartJson.tpo_add_price += totalCustomOptionPrice * e.quantity * 100;
        }
      }
    });

    // Merge duplicate line items
    const mergedLineItems = [];
    for (const item of lineItemsCustom) {
      const existing = mergedLineItems.find(
        (i) => i.variantId === item.variantId && 
              JSON.stringify(i.customAttributes) === JSON.stringify(item.customAttributes)
      );
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        mergedLineItems.push({ ...item });
      }
    }

    // Final mapping với multi-currency handling
    const lineItems = mergedLineItems.map((item) => {
      const i = { ...item };
      delete i.isFromOptionSet;

      if (shouldUseMultiCurrency) {
        const activeCurrency = i.customAttributes.find((a) => a.key === '_tpo_active_currency')?.value;
        const currencyRate = parseFloat(i.customAttributes.find((a) => a.key === '_tpo_currency_rate')?.value || 1);

        if (activeCurrency && currencyRate) {
          if (i.appliedDiscount?.value) i.appliedDiscount.value /= currencyRate;
          if (i.originalUnitPrice) i.originalUnitPrice /= currencyRate;
        }
      }

      // Clean up download URLs và price markers
      i.customAttributes = i.customAttributes.map((attr) => {
        if (typeof attr.value === 'string') {
          // Remove download URLs price marker
          if (attr.value.includes('apps/easify-po/apps/download')) {
            attr.value = attr.value.replace(/\[ \+(.*)\]/, '').trim();
          }
          // ⭐ Có thể remove price marker nếu cần
          // attr.value = attr.value.replace(/\[ \+(.*)\]/, '').trim();
        }
        return attr;
      });

      // ⭐ CRITICAL: Lọc bỏ bất kỳ hidden property nào còn sót lại
      i.customAttributes = i.customAttributes.filter(
        attr => !hiddenProperties.includes(attr.key)
      );

      return i;
    });

    return {
      line_items: lineItems,
      cart: priceModCart,
      customer: customerId,
      discount_code: discountCode,
      showDiscountBoxOnCheckout,
      locale: {
        shouldUseMultiCurrency,
        locale: storeLocale,
        currency: storeCurrency,
      },
    };
  }

  async handleClick(event) {
    event.preventDefault();

    const configs = this.getStoreConfigs();

    const cartRes = await fetch('/cart.js?app=tpo', { headers: { 'Content-Type': 'application/json' } });
    const cartJson = await cartRes.json();

    if (!cartJson.items.length) {
      alert('Cart Empty');
      return;
    }

    const cartInput = this.buildCartInput(cartJson, configs);

    const fetchOptions = {
        method: 'POST',
        headers: {
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
            'shop': Shopify.shop
        },
        body: JSON.stringify(cartInput)
    };

    fetch(`${cartInput.cart.easifyUrl}apps/draft-order`, fetchOptions).then(function(responseData) {
        if (responseData.status == '200') {
            console.log('EasifyPO redirect to checkout page 1');
            return responseData.json().then(function(res) {
                if ('string' == typeof res?.draftOrder?.invoiceUrl) {
                    window.location.href = res.draftOrder.invoiceUrl;
                }
            });
        } else {
            console.log('EasifyPO redirect to checkout page 2');
            // window.location.href = redirectUrl ? redirectUrl : '/checkout';
        }
    }).catch((error) => {
        console.log('Error:', error);
        // window.location.href = redirectUrl ? redirectUrl : '/checkout';
    });
  }
}

customElements.define('draft-order-button', DraftOrderButton);